/**
 * Métricas de insights do Instagram e do Facebook, e a regra que mais importa:
 * **ausente não é zero**.
 *
 * A Meta omite métrica por vários motivos — conta com menos de 100 seguidores,
 * dia ainda não consolidado (até 48h de atraso), métrica descontinuada. Zero é
 * "não aconteceu"; ausente é "não sei". Mostrar ausente como zero diz ao
 * cliente que ninguém viu o post dele. Mesma regra de `shared/whatsapp-billing.ts`
 * para custo.
 *
 * Por isso o banco só guarda linha para valor que a Meta DEU, e somar dias
 * devolve `null` quando não há nenhum, ou `parcial: true` quando falta algum.
 */

/**
 * Métricas diárias da conta do Instagram (`/{ig}/insights`, metric_type
 * total_value, period day). `follows`/`unfollows` saem do breakdown de
 * `follows_and_unfollows`; `followers_count` é foto diária do perfil.
 * `impressions` foi descontinuada (v22) — `views` é a sucessora.
 */
export const IG_ACCOUNT_METRICS = [
  "views",
  "reach",
  "accounts_engaged",
  "total_interactions",
  "likes",
  "comments",
  "shares",
  "saves",
  "profile_links_taps",
] as const;

/**
 * Métricas diárias da Página do Facebook (`/{page}/insights`, period day).
 * `page_impressions*` é obsoleta a partir da v25 — `page_media_view` substitui.
 * A Meta vem aposentando métricas de Página: o coletor pede em lote e, se a
 * Meta recusar o lote, uma a uma, guardando só as que vierem.
 */
export const FB_PAGE_METRICS = [
  "page_media_view",
  "page_total_media_view_unique",
  "page_post_engagements",
  "page_daily_follows_unique",
  "page_daily_unfollows_unique",
  "page_views_total",
  "page_follows",
] as const;

/**
 * YouTube (YouTube Analytics API, por dia). Os nomes reaproveitam os da Meta
 * quando o significado é o mesmo (visualizações, curtidas, novos inscritos =
 * `follows`), para os cartões e a soma funcionarem igual.
 */
export const YT_DAILY_METRICS = {
  views: "views",
  estimatedMinutesWatched: "watch_minutes",
  likes: "likes",
  comments: "comments",
  shares: "shares",
  subscribersGained: "follows",
  subscribersLost: "unfollows",
} as const;

/**
 * Página do LinkedIn (`organizationalEntityShareStatistics`, por dia). Perfil
 * pessoal não tem: o LinkedIn não libera números de perfil para apps comuns.
 */
export const LI_DAILY_METRICS = {
  impressionCount: "li_impressions",
  uniqueImpressionsCount: "li_unique_impressions",
  clickCount: "li_clicks",
  likeCount: "likes",
  commentCount: "comments",
  shareCount: "shares",
} as const;

/** Métricas por post do Instagram (feed e reels). Story some em 24h e fica de fora. */
export const IG_MEDIA_METRICS = ["views", "reach", "likes", "comments", "shares", "saved", "total_interactions"] as const;

/**
 * Métricas que são FOTO do dia (estoque), não fluxo: somar dias não faz
 * sentido — o total do período é o último valor.
 */
export const METRICAS_DE_ESTOQUE = new Set<string>(["followers_count", "page_follows", "tt_likes_total", "tt_video_count"]);

/**
 * Métricas de PESSOAS ÚNICAS por dia. Somar dias conta a mesma pessoa uma vez
 * por dia (1.000 pessoas alcançadas todo dia, as mesmas, viravam 28.000 no
 * mês). Sem o número deduplicado do período, o honesto é a média por dia — e
 * a tela diz isso no rótulo.
 */
export const METRICAS_UNICAS = new Set<string>([
  "reach",
  "accounts_engaged",
  "page_total_media_view_unique",
  "li_unique_impressions",
]);

export const METRIC_LABELS: Record<string, string> = {
  views: "Visualizações",
  reach: "Contas alcançadas por dia (média)",
  accounts_engaged: "Contas que interagiram por dia (média)",
  total_interactions: "Interações",
  likes: "Curtidas",
  comments: "Comentários",
  shares: "Compartilhamentos",
  saves: "Salvamentos",
  saved: "Salvamentos",
  profile_links_taps: "Toques nos links do perfil",
  follows: "Novos seguidores",
  unfollows: "Deixaram de seguir",
  followers_count: "Seguidores",
  page_media_view: "Visualizações",
  page_total_media_view_unique: "Pessoas que viram por dia (média)",
  page_post_engagements: "Interações nos posts",
  page_daily_follows_unique: "Novos seguidores",
  page_daily_unfollows_unique: "Deixaram de seguir",
  page_views_total: "Visitas à Página",
  page_follows: "Seguidores",
  watch_minutes: "Minutos assistidos",
  tt_likes_total: "Curtidas recebidas (total do perfil)",
  tt_video_count: "Vídeos no perfil",
  li_impressions: "Impressões",
  li_unique_impressions: "Pessoas alcançadas por dia (média)",
  li_clicks: "Cliques nos posts",
};

/** Onde a rede chama a mesma coisa por outro nome. */
const LABELS_POR_REDE: Partial<Record<RedeDeInsights, Record<string, string>>> = {
  youtube: { followers_count: "Inscritos", follows: "Novos inscritos", unfollows: "Cancelaram a inscrição" },
};

export function rotuloDaMetrica(rede: RedeDeInsights, metric: string): string {
  return LABELS_POR_REDE[rede]?.[metric] ?? METRIC_LABELS[metric] ?? metric;
}

export type RedeDeInsights = "instagram" | "facebook" | "tiktok" | "youtube" | "linkedin";

/** As que viram cartão no topo da tela, por rede, na ordem. */
export const CARTOES: Record<RedeDeInsights, string[]> = {
  instagram: ["views", "reach", "total_interactions", "follows", "followers_count", "profile_links_taps"],
  facebook: ["page_media_view", "page_total_media_view_unique", "page_post_engagements", "page_daily_follows_unique", "page_follows", "page_views_total"],
  // O TikTok só entrega totais do perfil (sem série por dia): a foto diária
  // que guardamos é o que permite ver a evolução.
  tiktok: ["followers_count", "tt_likes_total", "tt_video_count"],
  youtube: ["views", "watch_minutes", "follows", "followers_count", "likes", "comments"],
  linkedin: ["li_impressions", "li_unique_impressions", "li_clicks", "likes", "followers_count", "shares"],
};

/** As linhas do gráfico diário, por rede. */
export const SERIE: Record<RedeDeInsights, string[]> = {
  instagram: ["views", "reach", "total_interactions"],
  facebook: ["page_media_view", "page_total_media_view_unique", "page_post_engagements"],
  tiktok: ["followers_count"],
  youtube: ["views", "watch_minutes", "follows"],
  linkedin: ["li_impressions", "li_clicks", "likes"],
};

/** Redes em que guardamos os posts com números (ranking "Melhores posts"). */
export const TEM_RANKING_DE_POSTS: Record<RedeDeInsights, boolean> = {
  instagram: true,
  facebook: false,
  tiktok: true,
  youtube: true,
  linkedin: false,
};

/** Quantos dias para trás buscar a cada coleta: cobre o atraso de até 48h da Meta. */
export const DIAS_DE_RECOLETA = 3;
/** O YouTube Analytics fecha com 2–3 dias de atraso: relê uma janela maior. */
export const DIAS_DE_RECOLETA_POR_REDE: Partial<Record<RedeDeInsights, number>> = { youtube: 5 };

/**
 * Dias que a rede ainda não "fechou" (hoje incluso): os cartões param antes
 * deles, senão comparariam um período incompleto com um completo.
 */
export const DIAS_SEM_FECHAR: Record<RedeDeInsights, number> = {
  instagram: 2,
  facebook: 2,
  tiktok: 1,
  youtube: 3,
  linkedin: 2,
};
/** Na primeira coleta de uma conta, quanto histórico trazer. */
export const DIAS_DE_HISTORICO_INICIAL = 28;

export type Agregado = {
  /** Null = a Meta não deu valor em nenhum dia do período. */
  valor: number | null;
  /** true = faltou valor em algum dia do período (o total pode estar abaixo do real). */
  parcial: boolean;
};

/**
 * Total do período a partir dos valores por dia (`null` = dia sem valor).
 *
 * Fluxo (visualizações, alcance diário...) soma; estoque (seguidores) pega o
 * último dia que tem valor.
 */
export function agregar(metric: string, porDia: Array<number | null | undefined>): Agregado {
  const presentes = porDia.filter((v): v is number => typeof v === "number" && Number.isFinite(v));
  if (presentes.length === 0) return { valor: null, parcial: porDia.length > 0 };
  const parcial = presentes.length < porDia.length;
  if (METRICAS_DE_ESTOQUE.has(metric)) {
    const ultimo = [...porDia].reverse().find((v): v is number => typeof v === "number");
    return { valor: ultimo ?? null, parcial: false };
  }
  if (METRICAS_UNICAS.has(metric)) {
    // Média dos dias QUE TÊM valor — dia ausente não entra como zero.
    return { valor: Math.round(presentes.reduce((a, b) => a + b, 0) / presentes.length), parcial };
  }
  return { valor: presentes.reduce((a, b) => a + b, 0), parcial };
}

/**
 * Variação percentual entre dois períodos. Null quando não dá para afirmar —
 * inclusive quando um dos dois está PARCIAL: comparar 28 dias com 7 dias de
 * histórico mostraria um crescimento de 300% que não existiu.
 */
export function variacao(atual: Agregado | number | null, anterior: Agregado | number | null): number | null {
  const a = typeof atual === "object" && atual !== null ? atual : { valor: atual, parcial: false };
  const b = typeof anterior === "object" && anterior !== null ? anterior : { valor: anterior, parcial: false };
  if (a.parcial || b.parcial) return null;
  if (a.valor == null || b.valor == null || b.valor === 0) return null;
  return ((a.valor - b.valor) / b.valor) * 100;
}

/** Datas (YYYY-MM-DD, UTC) de `inicio` até `fim`, inclusive. */
export function diasEntre(inicio: string, fim: string): string[] {
  const out: string[] = [];
  const d = new Date(`${inicio}T00:00:00Z`);
  const f = new Date(`${fim}T00:00:00Z`);
  while (d <= f && out.length < 400) {
    out.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}

export function formatarNumero(v: number | null): string {
  if (v == null) return "—";
  return new Intl.NumberFormat("pt-BR", { notation: v >= 100_000 ? "compact" : "standard", maximumFractionDigits: 1 }).format(v);
}
